import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useConnectWallet, useWallets } from '@mysten/dapp-kit';

import { findWalletByIdentifier, WALLET_CONNECTION_ORDER } from '../utils/wallet.util';
import { WalletSliceKey, WalletSlice } from '../types/wallet-slice.type';
import { useSuiWallet } from './useSuiWallet';
import { useZkLoginWallet } from './useZkLoginWallet';

type AutoConnectStatus = 'disabled' | 'idle' | 'attempted';
const INITIAL_STATUS: Record<WalletSliceKey, AutoConnectStatus> = {
	suiWallet: 'idle',
	zkLoginWallet: 'idle',
};

const RETRY_DELAY_MS = 4000;

export function useInitWalletConnections(enabled: boolean = true): Record<WalletSliceKey, AutoConnectStatus> {
	const wallets = useWallets();
	const { mutateAsync: connectWallet } = useConnectWallet();
	const suiWalletSlice = useSuiWallet();
	const zkLoginWalletSlice = useZkLoginWallet();
	const [clientReady, setClientReady] = useState(false);
	const [tick, setTick] = useState(0);
	const inFlightRef = useRef<Record<WalletSliceKey, boolean>>({
		suiWallet: false,
		zkLoginWallet: false,
	});
	const attemptedKeyRef = useRef<Record<WalletSliceKey, string | null>>({
		suiWallet: null,
		zkLoginWallet: null,
	});
	const lastAttemptRef = useRef<Record<WalletSliceKey, number>>({
		suiWallet: 0,
		zkLoginWallet: 0,
	});

	useLayoutEffect(() => {
		if (!enabled) {
			setClientReady(false);
			return;
		}
		setClientReady(true);
	}, [enabled]);

	useEffect(() => {
		if (!enabled) return;
		const id = setInterval(() => setTick((t) => t + 1), RETRY_DELAY_MS);
		return () => clearInterval(id);
	}, [enabled]);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		// Re-run when slices hydrate or wallets become available so we don't miss a silent reconnect window.
		if (!clientReady || !wallets.length) {
			return;
		}

		const connectSlice = async (walletType: WalletSliceKey, slice: WalletSlice | null) => {
			if (slice?.currentWallet && slice?.currentAccount) {
				// Already connected.
				return;
			}

			const persistedWalletId = slice?.lastConnectedWalletName;
			const persistedAccount = slice?.lastConnectedAccountAddress;

			if (!persistedWalletId || !persistedAccount) {
				// Nothing to restore (yet) – wait for hydration or future updates.
				return;
			}

			const attemptKey = `${persistedWalletId}:${persistedAccount}`;
			const now = Date.now();
			const alreadyAttempted = attemptedKeyRef.current[walletType] === attemptKey;
			const throttled = now - lastAttemptRef.current[walletType] < RETRY_DELAY_MS;

			if ((alreadyAttempted && throttled) || inFlightRef.current[walletType]) {
				return;
			}

			const targetWallet = findWalletByIdentifier(wallets, persistedWalletId);
			if (!targetWallet) {
				// Wallet may not have loaded yet; try again when the wallet list changes.
				return;
			}

			inFlightRef.current[walletType] = true;
			lastAttemptRef.current[walletType] = now;

			try {
				await connectWallet({
					wallet: targetWallet,
					accountAddress: persistedAccount,
					silent: true,
				});
				attemptedKeyRef.current[walletType] = attemptKey;
			} catch {
				// Allow a future retry after the throttle window.
				attemptedKeyRef.current[walletType] = null;
			} finally {
				inFlightRef.current[walletType] = false;
			}
		};

		const init = async () => {
			for (const walletType of WALLET_CONNECTION_ORDER) {
				const slice = walletType === 'suiWallet' ? suiWalletSlice : zkLoginWalletSlice;
				await connectSlice(walletType, slice);
			}
		};

		void init();
	}, [clientReady, connectWallet, enabled, suiWalletSlice, tick, wallets, zkLoginWalletSlice]);

	return INITIAL_STATUS;
}
